import React from 'react';
import { Reveal } from './Reveal';
import { ArrowRight, Droplet, Heart, ShieldCheck, CheckCircle, Flame, Leaf } from 'lucide-react';

export const ProductsSection: React.FC = () => {
  return (
    <section id="products" className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-avo-main font-bold tracking-widest uppercase text-sm mb-3 block">Our Signature Product</span>
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-gray-900 mb-6 tracking-tight">
              Cold Press Crude Avocado Oil
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Cold-pressed from the finest Hass avocados grown in Nyeri, Kenya. Villa Avoils is your trusted avocado oil company for premium, sustainable products.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[minmax(200px,auto)]">
          {/* Main Image Card */}
          <Reveal className="md:col-span-8 md:row-span-2 h-full" delay={100}>
            <div className="group relative rounded-3xl overflow-hidden bg-white h-full min-h-[400px] shadow-sm hover:shadow-xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10"></div>
              <img 
                src="https://i.im.ge/2026/02/06/eiqeu1.avocado-oil.jpeg" 
                alt="Premium Avocado Oil" 
                className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 p-8 md:p-12 z-20 w-full">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md text-white text-sm font-medium mb-4 border border-white/30">
                  <Leaf className="w-4 h-4" /> 100% Pure Hass Avocado
                </div>
                <h3 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">Cold-Pressed Perfection</h3>
                <p className="text-white/90 max-w-xl text-lg leading-relaxed">
                  Extracted without heat or chemicals to preserve the raw nutrients, emerald green color, and rich buttery flavor.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Health Benefits Card */}
          <Reveal className="md:col-span-4" delay={200}>
            <div className="bg-white rounded-3xl p-8 h-full shadow-sm hover:shadow-md transition-all duration-300 border border-stone-100 flex flex-col justify-center group">
              <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6 text-emerald-600 group-hover:scale-110 transition-transform duration-300">
                <Heart className="w-7 h-7" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-3">Heart Healthy</h4>
              <p className="text-gray-600 leading-relaxed">
                Abundant in Oleic Acid (Omega-9) and Vitamin E, supporting cardiovascular health and reducing inflammation.
              </p>
            </div>
          </Reveal>

          {/* Culinary Uses Card */}
          <Reveal className="md:col-span-4" delay={300}>
            <div className="bg-avo-forest rounded-3xl p-8 h-full shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-center relative overflow-hidden group">
              <div className="absolute -right-6 -top-6 w-32 h-32 bg-white/5 rounded-full blur-2xl group-hover:bg-white/10 transition-colors duration-500"></div>
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6 text-avo-gold backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
                <Flame className="w-7 h-7" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-3">High Smoke Point</h4>
              <p className="text-white/80 leading-relaxed">
                Safe for cooking up to 271°C (520°F). Perfect for searing, roasting, and grilling without breaking down.
              </p>
            </div>
          </Reveal>

          {/* Purity Card */}
          <Reveal className="md:col-span-6" delay={400}>
            <div className="bg-white rounded-3xl p-8 h-full shadow-sm hover:shadow-md transition-all duration-300 border border-stone-100 flex items-center gap-6 group">
              <div className="w-16 h-16 bg-stone-50 rounded-full flex items-center justify-center shrink-0 text-gray-900 group-hover:bg-avo-main group-hover:text-white transition-colors duration-300">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Zero Additives</h4>
                <p className="text-gray-600">Every drop is rigorously lab-tested to ensure absolute purity and zero chemical interference.</p>
              </div>
            </div>
          </Reveal>

          {/* CTA Card */}
          <Reveal className="md:col-span-6" delay={500}>
            <div className="bg-stone-100 rounded-3xl p-8 h-full flex items-center justify-between group cursor-pointer hover:bg-stone-200 transition-colors duration-300">
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">Request a Sample</h4>
                <p className="text-gray-600">Experience the emerald difference.</p>
              </div>
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-sm text-avo-main group-hover:scale-110 transition-transform duration-300">
                <ArrowRight className="w-6 h-6" />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

